import React, { useEffect, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, fns } from "../firebase";
import { useSession } from "../auth.jsx";
import { GATES } from "../lib/rubric.js";
import { Eyebrow } from "../components/Bits.jsx";

export default function Gates() {
  const s = useSession();
  const [rows, setRows] = useState([]);
  const [issue, setIssue] = useState({});
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!s.profile?.cohortId) return;
    const q = query(collection(db, "gates"), where("cohortId", "==", s.profile.cohortId));
    return onSnapshot(q, (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, [s.profile?.cohortId]);

  const byGate = Object.keys(GATES).map((gid) => {
    const g = rows.filter((r) => r.gateId === gid);
    return {
      gid,
      name: GATES[gid].name,
      passed: g.filter((r) => r.status === "passed").length,
      failed: g.filter((r) => r.status === "failed").length,
      firstPass: g.length ? Math.round((g.filter((r) => r.status === "passed" && r.attempts === 1).length / g.length) * 100) : null,
    };
  });

  const byResident = {};
  for (const r of rows) (byResident[r.residentUid] ||= []).push(r);
  const ready = Object.entries(byResident)
    .filter(([, gs]) => gs.filter((g) => g.status === "passed").length === Object.keys(GATES).length)
    .map(([uid]) => uid);

  const [annual, setAnnual] = useState({});
  const publishAnnual = async () => {
    setMsg(null);
    try {
      const call = httpsCallable(fns, "publishAnnualEntry");
      await call({ cohortId: s.profile.cohortId, ...annual });
      setMsg("Published to the Annual at /annual" + (annual.prize ? ` — ${annual.prize} stamped.` : "."));
      setAnnual({});
    } catch (e) { setMsg(e.message); }
  };

  const doIssue = async (uid) => {
    setMsg(null);
    try {
      const call = httpsCallable(fns, "issueCredential");
      const r = await call({ residentUid: uid, award: issue[uid]?.award ?? "Certified", capstoneTitle: issue[uid]?.title ?? null });
      setMsg(`Issued ${r.data.credentialId} — now on the public Register at /register.`);
    } catch (e) { setMsg(e.message); }
  };

  return (
    <div>
      <Eyebrow>Mastery gates</Eyebrow>
      <h1 style={{ fontSize: 24, marginTop: 4 }}>Where the programme says no</h1>
      <p style={{ color: "var(--graphite)", maxWidth: "62ch" }}>
        First-pass rate is a curriculum signal, not a cohort signal. A gate under 40% first-pass means the
        preceding month under-prepared residents — take it to the week 26 revision window.
      </p>
      <table style={{ marginTop: 14 }}>
        <thead><tr><th>Gate</th><th>Name</th><th>Passed</th><th>Failed</th><th>First-pass rate</th></tr></thead>
        <tbody>
          {byGate.map((g) => (
            <tr key={g.gid}>
              <td className="mono">{g.gid}</td>
              <td>{g.name}</td>
              <td className="pass">{g.passed}</td>
              <td className={g.failed ? "fail" : ""}>{g.failed}</td>
              <td className="mono">{g.firstPass == null ? "—" : `${g.firstPass}%`}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {s.role === "lead" && (
        <div className="card" style={{ marginTop: 20 }}>
          <Eyebrow>Issue credentials · all six gates passed</Eyebrow>
          {ready.length === 0 && <p style={{ color: "var(--graphite)", fontSize: 14, marginTop: 6 }}>No resident has passed all six gates yet. The Register begins at Jury Day.</p>}
          {ready.map((uid) => (
            <div key={uid} className="crit row wrapflex" style={{ gap: 8 }}>
              <span className="mono" style={{ fontSize: 12 }}>{uid.slice(0, 10)}</span>
              <select onChange={(e) => setIssue({ ...issue, [uid]: { ...issue[uid], award: e.target.value } })} defaultValue="Certified">
                <option>Certified</option>
                <option>Certified with Distinction</option>
                <option>Certified with Highest Distinction</option>
              </select>
              <input type="text" placeholder="Capstone title" style={{ width: 220 }}
                onChange={(e) => setIssue({ ...issue, [uid]: { ...issue[uid], title: e.target.value } })} />
              <button className="btn" onClick={() => doIssue(uid)}>Issue to the Register</button>
            </div>
          ))}
          {msg && <p style={{ marginTop: 8, fontSize: 14 }}>{msg}</p>}
        </div>
      )}

      {s.role === "lead" && (
        <div className="card" style={{ marginTop: 14 }}>
          <Eyebrow>The Annual · publish a capstone (Jury Day)</Eyebrow>
          <div className="row wrapflex" style={{ gap: 8, marginTop: 8 }}>
            <input type="text" placeholder="Designer name" style={{ width: 180 }} value={annual.name ?? ""} onChange={(e) => setAnnual({ ...annual, name: e.target.value })} />
            <input type="text" placeholder="Capstone title" style={{ width: 220 }} value={annual.capstoneTitle ?? ""} onChange={(e) => setAnnual({ ...annual, capstoneTitle: e.target.value })} />
            <input type="text" placeholder="Case study URL" style={{ width: 220 }} value={annual.caseStudyUrl ?? ""} onChange={(e) => setAnnual({ ...annual, caseStudyUrl: e.target.value })} />
          </div>
          <div className="row wrapflex" style={{ gap: 8, marginTop: 8 }}>
            <input type="text" placeholder="Credential ID (verifies against Register)" style={{ width: 260 }} value={annual.credentialId ?? ""} onChange={(e) => setAnnual({ ...annual, credentialId: e.target.value })} />
            <select value={annual.prize ?? ""} onChange={(e) => setAnnual({ ...annual, prize: e.target.value || null })}>
              <option value="">No prize</option>
              <option>The Accessibility Prize</option>
              <option>The Anti-Slop Prize</option>
              <option>Best Defence</option>
              <option>The Provenance Prize</option>
              <option>The Stranger Test Prize</option>
            </select>
            <button className="btn" onClick={publishAnnual}>Publish to the Annual</button>
          </div>
          <p style={{ fontSize: 12, color: "var(--graphite)", marginTop: 6 }}>
            Prizes are decided by the external Jury; this desk records their decision. An entry citing a
            credential is refused unless that credential is active on the Register.
          </p>
        </div>
      )}
    </div>
  );
}
